<script>
  import { onDestroy, onMount } from "svelte";
  import {
    callQueue,
    activeSpec,
    unlockedSpecs,
    currentSpec,
    directContext,
    menuConfig,
    currMenu,
    dataInfo,
    currentElementLockGroup,
    viewSpecs,
  } from "../../editor/storage.js";
  import { firstLevelMenus } from "../../editor/menuSchema.js";
  import { deepcopy } from "../../editor/utility.js";
  import MarkLabelEditor from "../component/markLabelEditor.svelte";
  import AnnotationEditor from "../component/annotationEditor.svelte";
  import { makeRGBAString } from "../../editor/component/mark.js";
  import { unitString } from "../../fronto/utility.js";
  import {
    findLabel,
    unfindLabel,
    findAnnotation,
    unfindAnnotation,
  } from "../../editor/component/annotation.js";
  import { getAugmentation } from "../../editor/exploration/augment.js";
  // event functions
  export let inputFocusAction;
  export let inputBlurAction;

  let subMenu;
  let currMarkLabels;
  let currAnnos;
  let currAnnoIndex;
  let layerNumberLimit;
  function setValues() {
    currMarkLabels = [];
    $currentSpec?.fronto?.layer?.forEach((layer, li) => {
      let ti = 0;
      let lineLike = ["line", "area"].includes(layer.mark?.type);
      layer?.text?.forEach((text) => {
        if (text.type === "on-mark") {
          let textCopy = deepcopy(text);
          textCopy.layerIndex = li;
          textCopy.layerName = layer.name;
          textCopy.textIndex = ti;
          ti += 1;
          textCopy.items?.forEach((item) => {
            if (item.custom !== undefined) {
              item.customList = [];
              Object.keys(item.custom).forEach((c) => {
                item.customList.push({
                  value: c,
                  text: item.custom[c],
                });
              });
            }
          });
          textCopy.isLineLike = lineLike;
          currMarkLabels.push(textCopy);
        }
      });
    });
    currAnnos = [];
    $currentSpec?.fronto?.annotation?.items?.forEach((anno, ni) => {
      let textCopy = deepcopy(anno);
      textCopy.annoIndex = ni;
      currAnnos.push(textCopy);
    });

    if ($currentSpec?.fronto?.layout?.composition === "single") {
      if (
        $currentSpec?.fronto?.layout?.row?.length >= 2 ||
        $currentSpec?.fronto?.layout?.column?.length >= 2
      )
        layerNumberLimit = 1;
      else layerNumberLimit = Infinity;
    } else if ($currentSpec?.fronto?.layout?.composition === "repeated") {
      layerNumberLimit = 1;
    } else {
      layerNumberLimit = Infinity;
    }
  }

  function customListToObj(list) {
    let obj = {};
    list.forEach((c) => {
      obj[c.value] = c.text;
    });
    return obj;
  }

  // mark labels
  function createNewMarkLabel(event, newDesign) {
    if (newDesign.layer && newDesign.field) {
      let base = newDesign.layer.text?.length || 0;
      let name = "label_" + (base || 0);
      let existing_ids = [];
      $viewSpecs.forEach((spec) => {
        spec.fronto?.layer?.forEach((layer) => {
          existing_ids.push(...(layer.text?.map((d) => d.name) || []));
        });
      });
      let cnt = 2;
      while (existing_ids.includes(name)) {
        cnt++;
        id = "label_" + (base + cnt);
      }
      let rule = {
        category: "manual",
        description: "Add new mark labels for " + newDesign.field,
        specifier: { role: "mark" },
        action: "add",
        option: {
          role: "label",
          field: newDesign.field,
          id: name,
          items: [{}],
          orient: "top-center",
          anchorAlign: "middle",
        },
      };
      if (newDesign.layer.name !== undefined)
        rule.specifier.id = newDesign.layer.name;
      else if (newDesign.layer.index !== undefined)
        rule.specifier.index = newDesign.layer.index;
      if (newDesign.values.constructor.name === "String") {
        rule.option.values = newDesign.values.split(",");
        if ($dataInfo.stats[newDesign.field].rawType === "Number") {
          rule.option.values = rule.option.values.map((d) =>
            parseFloat(d.trim())
          );
        } else {
          rule.option.values = rule.option.values.map((d) => d.trim());
        }
      } else if (newDesign.values.constructor.name === "Array") {
        rule.option.values = rule.option.values;
      }

      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
      cancelSubMenu();
    }
  }

  function updateMarkLabel(event, role, label, option) {
    let rule;
    if (role === "lock") {
      updateLock(event, "markLabel", label);
      return;
    } else if (role === "layer") {
      let newLayerName = event.target.value;
      if (label.field && (label.layerName || label.layerIndex !== undefined))
        rule = {
          category: "manual",
          description: "Move to other layer",
          specifier: { role: "mark.label", field: label.field, layer: {} },
          oction: "replace",
          option: {
            to: {
              layer: { id: newLayerName },
            },
          },
        };
      if (label.name) rule.specifier.id = label.name;
      if (label.layerName) rule.specifier.layer.id = label.layerName;
      else if (label.layerIndex !== undefined)
        rule.specifier.layer.index = label.layerIndex;
    } else if (role === "label.items" && event === "duplicate") {
      let dup;
      if (label.items) dup = deepcopy(label.items[label.items.length - 1]);
      else dup = {};
      rule = {
        category: "manual",
        description: "Add a new line for mark label for " + field,
        specifier: { role: "mark.label", field: label.field, layer: {} },
        action: "add",
        option: {
          items: dup,
        },
      };
      if (label.name) rule.specifier.id = label.name;
      if (label.layerName) rule.specifier.layer.id = label.layerName;
      else if (label.layerIndex !== undefined)
        rule.specifier.layer.index = label.layerIndex;
    } else if (role === "mark.label" && event === "delete") {
      rule = {
        category: "manual",
        description: "Delete a mark label for " + field,
        specifier: { role: "mark.label", field: label.field, layer: {} },
        action: "remove",
      };
      if (label.name) rule.specifier.id = label.name;
      if (label.layerName) rule.specifier.layer.id = label.layerName;
      else if (label.layerIndex !== undefined)
        rule.specifier.layer.index = label.layerIndex;
      if (label.textIndex) rule.specifier.index = label.textIndex;
    } else {
      let newValue;
      if (["label.field", "label.orient", "label.anchor"].includes(role)) {
        newValue = event.target.value;
      } else if (role === "label.values") {
        if (event.target.value?.length > 0) {
          newValue = event.target.value?.split(",")?.map((a) => a.trim());
        } else {
          newValue = null;
        }
      } else if (["label.dx", "label.dy", "label.angle"].includes(role)) {
        newValue = parseFloat(event.target.value);
      } else if (["label.externalize", "label.numbering"].includes(role)) {
        newValue = event.target.checked || null;
      } else if (
        role.match(
          /^label\.items\.[0-9]+(\.(|values|align|fontWeight|fontStroke|format|expression|width|fontSize|opacity|align|fontWeight|fontStroke|format|expression|custom|content|color))?$/
        )
      ) {
        let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
        if (itemIndex !== undefined) {
          if (role.endsWith(".values")) {
            if (event.target.value?.length > 0) {
              newValue = event.target.value?.split(",")?.map((a) => a.trim());
            } else {
              newValue = null;
            }
          } else if (
            role.endsWith(".width") ||
            role.endsWith(".fontSize") ||
            role.endsWith(".opacity")
          ) {
            newValue = parseFloat(event.target.value);
          } else if (
            role.endsWith(".align") ||
            role.endsWith(".fontWeight") ||
            role.endsWith(".format") ||
            role.endsWith(".expression") ||
            role.endsWith(".content")
          ) {
            newValue = event.target.value;
          } else if (role.endsWith(".color") || role.endsWith(".fontStroke")) {
            if (event.target?.value?.startsWith("$")) {
              newValue = event.target.value;
            } else {
              newValue = makeRGBAString(
                event.detail.r,
                event.detail.g,
                event.detail.b,
                event.detail.a
              );
            }
          } else if (role.endsWith(".custom")) {
            newValue = customListToObj(
              event?.constructor.name === "Array"
                ? event
                : label.items?.[itemIndex]?.customList || []
            );
            if (event.newCustomValue !== undefined) {
              newValue[event.newCustomValue] = event.newCustomText;
            }
          } else if (
            event === "remove" &&
            role.match(/^label\.items\.[0-9]+/)
          ) {
            newValue = null;
          }
        }
      } else if (role === "label.tick.ticks") {
        newValue = event.target.checked || null;
      } else if (
        [
          "label.tick.offset",
          "label.tick.strokeWidth",
          "label.tick.opacity",
        ].includes(role)
      ) {
        newValue = parseFloat(event.target.value);
      } else if (role === "label.tick.stroke") {
        newValue = makeRGBAString(
          event.detail.r,
          event.detail.g,
          event.detail.b,
          event.detail.a
        );
      } else if (role === "label.tick.dash") {
        newValue = event.target.value.split(",").map((d) => parseFloat(d));
      }
      if (newValue !== undefined || newValue == null) {
        rule = {
          description: "Edit mark label for " + label.field + " ",
          specifier: {
            role: "mark.label",
            field: label.field,
            layer: { id: label.layerName },
          },
          action: "modify",
          option: {},
        };
        if (label.name) rule.specifier.id = label.name;
        if (label.layerName) rule.specifier.layer.id = label.layerName;
        else if (label.layerIndex !== undefined)
          rule.specifier.layer.index = label.layerIndex;
        if (
          [
            "label.field",
            "label.values",
            "label.orient",
            "label.anchor",
            "label.dx",
            "label.dy",
            "label.angle",
          ].includes(role)
        ) {
          let optionKey = role.split(".")[1];
          rule.description += optionKey;
          rule.option[optionKey] = newValue;
        } else if (role === "label.externalize") {
          rule.description += "external ";
          if (newValue == null) {
            rule.description += "(unset)";
            rule.option = { external: null };
          } else {
            rule.description += "(set)";
            rule.option = {
              external: { position: "bottom", numbering: false },
            };
          }
        } else if (role === "label.numbering") {
          rule.description += "numbering ";
          if (newValue == null) {
            rule.description += "(unset)";
            rule.option = { numbering: false };
          } else {
            rule.description += "(set)";
            rule.option = { numbering: true };
          }
        } else if (
          role.match(
            /^label\.items\.[0-9]+(\.(|values|align|fontWeight|fontStroke|format|expression|width|fontSize|opacity|align|fontWeight|fontStroke|format|expression|content|custom|color))?$/
          )
        ) {
          let itemIndex = parseInt(role.match(/[0-9]+/gi)[0]);
          rule.description += `text (${itemIndex}) `;
          if (itemIndex !== undefined) {
            if (
              role.endsWith(".values") ||
              role.endsWith(".width") ||
              role.endsWith(".fontSize") ||
              role.endsWith(".opacity") ||
              role.endsWith(".align") ||
              role.endsWith(".fontWeight") ||
              role.endsWith(".fontStroke") ||
              role.endsWith(".color") ||
              role.endsWith(".fontStroke") ||
              role.endsWith(".format") ||
              role.endsWith(".expression") ||
              role.endsWith(".custom") ||
              role.endsWith(".content")
            ) {
              let optionKey = role.split(".")[3];
              rule.description += " (" + optionKey + ")";
              rule.option = { items: { index: itemIndex } };
              rule.option.items[optionKey] = newValue;
            } else if (
              event === "delete" &&
              role.match(/^label\.items\.[0-9]+/)
            ) {
              rule.description = rule.description.replace("Edit ", "Remove ");
              rule.action = "remove";
              rule.option = { items: { index: itemIndex } };
            }
          }
        } else if (role === "label.tick.ticks") {
          rule.description += "ticks ";
          if (newValue) {
            rule.description += "(set)";
            rule.option = { tick: true };
          } else {
            rule.description += "(unset)";
            rule.option = { tick: null };
          }
        } else if (
          [
            "label.tick.offset",
            "label.tick.strokeWidth",
            "label.tick.opacity",
            "label.tick.stroke",
            "label.tick.dash",
          ].includes(role)
        ) {
          let optionKey = role.split(".")[2];
          rule.description += "ticks " + optionKey;
          rule.specifier.role = "mark.label.tick";
          rule.option[optionKey] = newValue;
        }
      }
    }
    if (rule) {
      rule.category = "manual";
      callQueue.update((queue) => {
        queue.add([rule], $unlockedSpecs).apply();
        return queue;
      });
    }
  }

  // Annotations
  function createNewAnnotation() {
    let base = currAnnos?.length || 0;
    let id = "annotation_" + (base || 0);
    let existing_ids = [];
    $viewSpecs.forEach((spec) => {
      existing_ids.push(
        ...(spec.fronto?.annotation?.items?.map((d) => d.name) || [])
      );
    });
    let cnt = 2;
    while (existing_ids.includes(id)) {
      cnt++;
      id = "annotation_" + (base + cnt);
    }
    let rule = {
      category: "manual",
      description: "Add a new annotation",
      specifier: { role: "view" },
      action: "add",
      option: {
        role: "annotation",
        annotation: {
          text: [{ text: "Annotation", textAlign: "left" }],
          id,
          x: "25%",
          y: "25%",
          width: 200,
        },
      },
    };
    callQueue.update((queue) => {
      queue.add([rule], $unlockedSpecs).apply();
      return queue;
    });
  }

  function updateAnnotation(event, role, anno) {
    if (role === "lock") {
      updateLock(event, "annotation", anno);
      return;
    } else if (role === "lock-position") {
      updateLock(event, "annotation.position", anno);
      return;
    } else if (
      event === "delete" &&
      role.match(/^annotation(\.text\.[0-9]+)?$/)
    ) {
      let rule = {
        category: "manual",
        specifier: { role: "annotation" },
        action: "remove",
      };
      if (anno.name) {
        rule.specifier.id = anno.name;
      } else if (anno.annoIndex !== undefined) {
        rule.specifier.index = anno.annoIndex;
      }
      if (role === "annotation") {
        rule.description = "Delete annotation " + anno.annoIndex;
      } else if (role.match(/^annotation\.text\.[0-9]+$/)) {
        let index = role.split(".")[2];
        rule.description = "Delete annotation line " + index;
        rule.option = {
          text: { index },
        };
      }
      if (rule.description) {
        callQueue.update((queue) => {
          queue.add([rule], $unlockedSpecs).apply();
          return queue;
        });
      }
    } else if (event === "duplicate" && role === "annotation.text") {
      let rule = {
        category: "manual",
        description: "Add a new annotation line",
        specifier: { role: "annotation" },
        action: "add",
      };
      if (anno.name) {
        rule.specifier.id = anno.name;
      } else if (anno.annoIndex !== undefined) {
        rule.specifier.index = anno.annoIndex;
      }
      let dup = deepcopy(anno.text[anno.text.length - 1]);
      rule.option = {
        text: dup,
      };
      if (rule.description) {
        callQueue.update((queue) => {
          queue.add([rule], $unlockedSpecs).apply();
          return queue;
        });
      }
    } else {
      let newValue;
      if (
        [
          "annotation.name",
          "annotation.x",
          "annotation.y",
          "annotation.dx",
          "annotation.dy",
          "annotation.box.strokeStyle",
          "annotation.orient",
          "annotation.anchor",
          "annotation.tick.cornering",
        ].includes(role)
      ) {
        newValue = event.target.value;
      } else if (
        [
          "annotation.width",
          "annotation.height",
          "annotation.rotate",
          "annotation.lineHeight",
          "annotation.box.radius",
          "annotation.box.width",
          "annotation.box.strokeWidth",
          "annotation.box.strokeOpacity",
          "annotation.box.opacity",
          "annotation.tick.from.dx",
          "annotation.tick.from.dy",
          "annotation.tick.to.dx",
          "annotation.tick.to.dy",
        ].includes(role)
      ) {
        newValue = parseFloat(event.target.value);
      } else if (
        [
          "annotation.external",
          "annotation.tick",
          "annotation.numbering",
          "annotation.axisAssist",
        ].includes(role)
      ) {
        newValue = event.target.checked || null;
      } else if (role === "annotation.box.padding") {
        let pos = event.target.getAttribute("name");
        let existing = anno.box?.padding?.split(" ") || [0, 0, 0, 0];
        if (pos === "padding-top") existing[0] = event.target.value;
        else if (pos === "padding-bottom") existing[2] = event.target.value;
        else if (pos === "padding-left") existing[1] = event.target.value;
        else if (pos === "padding-right") existing[3] = event.target.value;
        newValue = existing.map((d) => unitString(d || 0, "px")).join(" ");
        console.log(newValue);
      } else if (
        ["annotation.box.stroke", "annotation.box.fill"].includes(role)
      ) {
        newValue = makeRGBAString(
          event.detail.r,
          event.detail.g,
          event.detail.b,
          event.detail.a
        );
      } else if (
        role.match(
          /^annotation\.text\.[0-9]+\.(text|textAlign|fontSize|fontWeight|color|opacity|overflow|lineHeight)$/
        )
      ) {
        if (
          role.endsWith("text") ||
          role.endsWith("textAlign") ||
          role.endsWith("fontWeight") ||
          role.endsWith("overflow")
        ) {
          newValue = event.target.value;
        } else if (
          role.endsWith("fontSize") ||
          role.endsWith("opacity") ||
          role.endsWith("lineHeight")
        ) {
          newValue = parseFloat(event.target.value);
        } else if (role.endsWith("color")) {
          newValue = makeRGBAString(
            event.detail.r,
            event.detail.g,
            event.detail.b,
            event.detail.a
          );
        }
      } else if (role === "annotation.dataAnchor") {
        newValue = event;
      }
      if (newValue !== undefined || newValue === null) {
        let rule = {
          description:
            "Edit annotation " +
            (anno.annoIndex !== undefined ? anno.annoIndex : "") +
            " ",
          specifier: { role: "annotation" },
          action: "modify",
        };
        if (anno.name) rule.specifier.id = anno.name;
        else if (anno.annoIndex !== undefined)
          rule.specifier.index = anno.annoIndex;
        if (
          [
            "annotation.name",
            "annotation.x",
            "annotation.y",
            "annotation.dx",
            "annotation.dy",
            "annotation.width",
            "annotation.height",
            "annotation.rotate",
            "annotation.orient",
            "annotation.anchor",
            "annotation.dataAnchor",
          ].includes(role)
        ) {
          let optionKey = role.split(".")[1];
          rule.option = {};
          rule.description += optionKey;
          rule.option[optionKey] = newValue;
        } else if (
          [
            "annotation.box.radius",
            "annotation.box.width",
            "annotation.box.strokeWidth",
            "annotation.box.strokeOpacity",
            "annotation.box.opacity",
            "annotation.box.padding",
            "annotation.box.stroke",
            "annotation.box.strokeStyle",
            "annotation.box.fill",
          ].includes(role)
        ) {
          let optionKey = role.split(".")[2];
          rule.description += "box " + optionKey;
          rule.option = { box: {} };
          rule.option.box[optionKey] = newValue;
        } else if (
          [
            "annotation.external",
            "annotation.tick",
            "annotation.numbering",
            "annotation.axisAssist",
          ].includes(role)
        ) {
          let optionKey = role.split(".")[1];
          if (newValue == null) {
            rule.description += optionKey + " (unset)";
            rule.option = {};
            rule.option[optionKey] = null;
          } else {
            rule.description += optionKey + " (set)";
            rule.option = {};
            rule.option[optionKey] = true;
          }
        } else if (
          role.match(
            /^annotation\.text\.[0-9]+\.(text|textAlign|fontSize|fontWeight|color|opacity|overflow|lineHeight)$/
          )
        ) {
          let ti = role.split(".")[2];
          let optionKey = role.split(".")[3];
          rule.option = { text: { index: ti } };
          rule.description += " line (" + ti + ") " + optionKey;
          if (
            [
              "text",
              "textAlign",
              "fontWeight",
              "overflow",
              "fontSize",
              "opacity",
              "color",
              "lineHeight",
            ].includes(optionKey)
          ) {
            rule.option.text[optionKey] = newValue;
          }
        } else if (
          [
            "annotation.tick.from.dx",
            "annotation.tick.from.dy",
            "annotation.tick.to.dx",
            "annotation.tick.to.dy",
          ].includes(role)
        ) {
          let [_, __, dirKey, posKey] = role.split(".");
          rule.description +=
            "tick position " +
            (dirKey === "from" ? "from mark " : "to text ") +
            "(" +
            posKey +
            ")";
          rule.option = { tick: {} };
          rule.option.tick[dirKey] = {};
          rule.option.tick[dirKey][posKey] = newValue;
        } else if (role === "annotation.tick.cornering") {
          rule.description += "tick cornering";
          rule.option = {
            tick: { cornering: newValue },
          };
        } else {
          console.log(role, newValue);
        }
        // application
        if (rule) {
          rule.category = "manual";
          callQueue.update((queue) => {
            queue.add([rule], $unlockedSpecs).apply({
              afterUpdateAll: () => {
                if (role === "annotation.external") {
                  getAugmentation(
                    role,
                    rule,
                    anno.visibility?.externalize,
                    newValue,
                    $currentSpec,
                    $unlockedSpecs,
                    anno
                  );
                } else if (role === "annotation.dx") {
                  getAugmentation(
                    role,
                    rule,
                    anno.dx,
                    newValue,
                    $currentSpec,
                    $unlockedSpecs,
                    anno
                  );
                } else if (role === "annotation.dy") {
                  getAugmentation(
                    role,
                    rule,
                    anno.dy,
                    newValue,
                    $currentSpec,
                    $unlockedSpecs,
                    anno
                  );
                }
              },
            });
            return queue;
          });
        }
      }
    }
  }

  function setSubMenu(s, i) {
    subMenu = s;
    currAnnoIndex = i;
  }
  function cancelSubMenu() {
    subMenu = undefined;
    currAnnoIndex = undefined;
  }
  // direct manipulation
  menuConfig.subscribe((config) => {
    if (config?.subMenu === "mark-label" && config.layerIndex !== undefined) {
      setValues();
      let ti = currMarkLabels
        ?.map((label, li) =>
          label.layerIndex === config.layerIndex &&
          label.textIndex == config.textIndex
            ? li
            : undefined
        )
        .filter((d) => d !== undefined)[0];
      if (ti !== undefined) setSubMenu("mark-label", ti);
    } else if (
      config?.subMenu === "annotation" &&
      config.annoIndex !== undefined
    ) {
      setValues();
      setSubMenu("annotation", config.annoIndex);
    }
  });
  // pipelines
  onMount(() => {
    setValues();
  });
  currentSpec.subscribe(() => {
    setValues();
  });
  onDestroy(() => {
    cancelSubMenu();
  });

  function updateLock(event, role, detail) {
    if ($currentElementLockGroup.length > 0) {
      let newValue = event.target.checked;
      if (newValue !== undefined && role && detail) {
        let rule;
        if (role === "markLabel") {
          rule = {
            description:
              (newValue ? "Lock" : "Unlock") +
              " mark label for " +
              detail.field,
            specifier: {
              role: "mark.label",
              field: detail.field,
              layer: {},
            },
            action: "lock",
            option: {
              label: newValue,
            },
          };
          if (detail.layerName) rule.specifier.layer.id = detail.layerName;
          else if (detail.layerIndex !== undefined)
            rule.specifier.layer.index = detail.layerIndex;
        } else if (role === "annotation") {
          rule = {
            description:
              (newValue ? "Lock" : "Unlock") +
              " annotation " +
              (detail.annoIndex !== undefined ? detail.annoIndex : ""),
            specifier: { role: "annotation" },
            action: "lock",
            option: {
              annotation: newValue,
            },
          };
          if (detail.name) rule.specifier.id = detail.name;
          else if (detail.annoIndex !== undefined)
            rule.specifier.index = detail.annoIndex;
        } else if (role === "annotation.position") {
          rule = {
            description:
              (newValue ? "Lock" : "Unlock") +
              " annotation " +
              (detail.annoIndex !== undefined ? detail.annoIndex : "") +
              "'s position",
            specifier: { role: "annotation" },
            action: "lock",
            option: {
              position: newValue,
            },
          };
          if (detail.name) rule.specifier.id = detail.name;
          else if (detail.annoIndex !== undefined)
            rule.specifier.index = detail.annoIndex;
        }

        if (rule) {
          rule.category = "lock";
          callQueue.update((queue) => {
            queue.add([rule], $currentElementLockGroup).apply();
            return queue;
          });
        }
      }
    }
  }
</script>

<section class="second-level-menu">
  <h2>
    {firstLevelMenus["annotation"]?.title || "Annotation"}
  </h2>

  {#if $activeSpec == -1}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Create or select an artboard!</p>
      </div>
    </div>
  {:else if !$unlockedSpecs.includes($currentSpec.uid)}
    <div class="dupo-menu-pane mb-2 warn-msg pt-2" style="margin-top: -0.5rem;">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Unlock this artboard first!</p>
      </div>
    </div>
  {:else if !subMenu}
    <div class="dupo-menu-pane mb-2">
      <div class="dupo-menu-pane-row">
        <p class="text-sm">Select annotations to edit.</p>
      </div>
    </div>
    <!-- Mark Labels -->
    <div class="dupo-menu-pane">
      <div class="dupo-menu-pane-row">
        <h3 class="h-sub">Mark labels</h3>
      </div>
    </div>

    {#if currMarkLabels.length > 0}
      <div class="dupo-menu-pane mb-2">
        {#each currMarkLabels as label, li}
          <div class="dupo-menu-pane-form-row">
            <div
              class="menu-second-level menu-with-buttons"
              on:mouseover={() => {
                findLabel($currentSpec.uid, label);
              }}
              on:focus={() => {
                findLabel($currentSpec.uid, label);
              }}
              on:mouseout={() => {
                unfindLabel();
              }}
              on:blur={() => {
                unfindLabel();
              }}
            >
              <div class="text-truncate" style="user-select:none;">
                <i class="bi bi-tags me-1" />
                {label.name} /
                {label.layerName || "Layer " + label.layerIndex}<i
                  class="bi bi-caret-right-fill mx-1"
                />
                {label.field}
              </div>
              <div class="dupo-pane-list-button">
                <button
                  type="button"
                  on:click={() => {
                    setSubMenu("mark-label", li);
                  }}
                  ><i
                    class="bi bi-pencil-fill"
                    alt="{`Edit mark labels for ${label.field}`}}"
                  /></button
                >
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <div class="dupo-menu-pane">
      <div class="dupo-menu-pane-form-row">
        <button
          type="button"
          class="menu-second-level"
          on:click={() => {
            setSubMenu("mark-label", "new");
          }}
          ><i class="bi bi-plus-circle me-1" /> Add a new mark label
        </button>
      </div>
    </div>

    <div class="pane-divider" />

    <!-- Nondata annotations -->
    <div class="dupo-menu-pane mt-3">
      <div class="dupo-menu-pane-row">
        <h3 class="h-sub">Annotations/captions</h3>
      </div>
    </div>

    {#if currAnnos.length > 0}
      <div class="dupo-menu-pane mb-2">
        {#each currAnnos as anno, ai}
          <div class="dupo-menu-pane-form-row">
            <div
              class="menu-second-level menu-with-buttons"
              on:mouseover={() => {
                findAnnotation($currentSpec.uid, anno);
              }}
              on:focus={() => {
                findAnnotation($currentSpec.uid, anno);
              }}
              on:mouseout={() => {
                unfindAnnotation();
              }}
              on:blur={() => {
                unfindAnnotation();
              }}
            >
              <div class="text-truncate" style="user-select:none;">
                <i class="bi bi-chat-square me-1" />
                {anno.name || "Annotation " + anno.annoIndex}
                {": " + anno.text?.[0]?.text?.substring(0, 15) || ""}
              </div>
              <div class="dupo-pane-list-button">
                <button
                  type="button"
                  on:click={() => {
                    setSubMenu("annotation", ai);
                  }}
                  ><i
                    class="bi bi-pencil-fill"
                    alt="{`Edit ${anno.field} annotation`}}"
                  /></button
                >
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <div class="dupo-menu-pane">
      <div class="dupo-menu-pane-form-row">
        <button
          type="button"
          class="menu-second-level"
          on:click={() => {
            createNewAnnotation();
          }}
          ><i class="bi bi-plus-circle me-1" /> Add a annotation
        </button>
      </div>
    </div>
  {:else if subMenu === "mark-label"}
    <MarkLabelEditor
      {inputFocusAction}
      {inputBlurAction}
      label={currAnnoIndex === "new" ? "new" : currMarkLabels[currAnnoIndex]}
      {updateMarkLabel}
      {layerNumberLimit}
      isLineLike={currMarkLabels.isLineLike}
      creator={createNewMarkLabel}
      afterDelete={() => {
        if ($directContext) currMenu.set("selection");
        else cancelSubMenu();
      }}
    />
    <div class="dupo-menu-pane mb-3">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else cancelSubMenu();
          }}
          ><i class="bi bi-arrow-left me-1" /> Go back
        </button>
      </div>
    </div>
  {:else if subMenu === "annotation"}
    <AnnotationEditor
      {inputFocusAction}
      {inputBlurAction}
      annotation={currAnnos[currAnnoIndex]}
      {updateAnnotation}
      afterDelete={() => {
        cancelSubMenu();
        if ($directContext) currMenu.set("selection");
      }}
    />
    <div class="dupo-menu-pane mb-3">
      <div class="dupo-menu-pane-button-row">
        <button
          type="button"
          on:click={() => {
            if ($directContext) currMenu.set("selection");
            else cancelSubMenu();
          }}
          ><i class="bi bi-arrow-left me-1" /> Go back
        </button>
      </div>
    </div>
  {/if}
</section>
